// src/pages/teacher/TeacherInvoiceCapture.tsx
// סריקת חשבוניות — צילום + קובץ + Gemini OCR + אימות

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, FolderOpen, FileCheck, Loader2, CheckCircle, AlertTriangle, XCircle, RotateCcw, Save, ArrowRight, ArrowLeft, Sparkles, Edit3, Download } from 'lucide-react';
import { teacherApi } from '../../services/api';
import FormLabel from '../../components/common/FormLabel';
import HelperText from '../../components/common/HelperText';
import { teacherInvoiceHelpTexts } from '../../constants/helpTexts';
import { teacherColors } from '../../styles/colors';

const colors = {
  ...teacherColors,
  success: '#22c55e',
  successLight: '#dcfce7',
  warning: '#f59e0b',
  warningLight: '#fef3c7',
  error: '#ef4444',
  errorLight: '#fee2e2',
  background: '#faf5ff',
};

interface OcrResult {
  vendorName?: string;
  vendorVatNumber?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  totalBeforeVat?: number;
  vatAmount?: number;
  totalAmount?: number;
  items?: Array<{
    description?: string;
    quantity?: number;
    unitPrice?: number;
    totalPrice?: number;
  }>;
  overallConfidence: number;
  processedBy?: string;
}

interface OcrField {
  key: string;
  label: string;
  value: string;
  type: 'text' | 'number' | 'date';
}

export default function TeacherInvoiceCapture() {
  const navigate = useNavigate();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [ocrResult, setOcrResult] = useState<OcrResult | null>(null);
  const [ocrError, setOcrError] = useState<string | null>(null);
  const [expensePublicId, setExpensePublicId] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [editMode, setEditMode] = useState(false);
  // Track list of all scanned invoices in this session
  const [scannedInvoices, setScannedInvoices] = useState<Array<{
    vendorName?: string;
    totalAmount?: number;
    invoiceNumber?: string;
    invoiceDate?: string;
    blobUrl?: string;
    ocrResult: OcrResult;
  }>>([]);

  // Editable fields
  const [editVendorName, setEditVendorName] = useState('');
  const [editVatNumber, setEditVatNumber] = useState('');
  const [editInvoiceNumber, setEditInvoiceNumber] = useState('');
  const [editInvoiceDate, setEditInvoiceDate] = useState('');
  const [editTotalAmount, setEditTotalAmount] = useState('');

  const handleCameraCapture = () => {
    cameraInputRef.current?.click();
  };

  const handleFilePick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setOcrResult(null);
      setOcrError(null);
      setExpensePublicId(null);
      setVerified(false);
      setEditMode(false);

      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleUploadAndScan = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setOcrError(null);
    setUploadProgress(10);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(p => Math.min(p + 15, 85));
      }, 500);

      const result = await teacherApi.uploadInvoice(selectedFile);
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.success && result.data) {
        setExpensePublicId(result.data.expensePublicId);
        setBlobUrl(result.data.blobUrl || null);

        // Check for OCR error (upload succeeded but OCR failed)
        if (result.data.ocrError) {
          setOcrError(result.data.ocrError);
          return;
        }

        if (result.data.ocrResult) {
          const ocr = result.data.ocrResult;
          setOcrResult(ocr);
          // Initialize edit fields
          setEditVendorName(ocr.vendorName || '');
          setEditVatNumber(ocr.vendorVatNumber || '');
          setEditInvoiceNumber(ocr.invoiceNumber || '');
          setEditInvoiceDate(ocr.invoiceDate ? ocr.invoiceDate.split('T')[0] : '');
          setEditTotalAmount(ocr.totalAmount?.toString() || '');
        } else {
          setOcrError('הסריקה לא הצליחה לזהות פרטי חשבונית. נסה תמונה ברורה יותר.');
        }
      } else {
        setOcrError(result.message || 'שגיאה בסריקת חשבונית');
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      setOcrError(err?.response?.data?.message || err?.message || 'שגיאה בהעלאת חשבונית');
    } finally {
      setUploading(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      let finalOcr = ocrResult;
      if (expensePublicId) {
        // Server-side verify (linked to an expense)
        const corrections: any = {};
        if (editVendorName !== (ocrResult?.vendorName || ''))
          corrections.vendorName = editVendorName;
        if (editVatNumber !== (ocrResult?.vendorVatNumber || ''))
          corrections.vendorVatNumber = editVatNumber;
        if (editInvoiceNumber !== (ocrResult?.invoiceNumber || ''))
          corrections.invoiceNumber = editInvoiceNumber;
        if (editInvoiceDate !== (ocrResult?.invoiceDate?.split('T')[0] || ''))
          corrections.invoiceDate = editInvoiceDate;
        if (editTotalAmount !== (ocrResult?.totalAmount?.toString() || ''))
          corrections.totalAmount = parseFloat(editTotalAmount) || null;

        const result = await teacherApi.verifyInvoice(expensePublicId, corrections);
        if (result.success) {
          setVerified(true);
          setEditMode(false);
        } else {
          setOcrError(result.message || 'שגיאה באימות');
          return;
        }
      } else {
        // Standalone scan — no expense in DB yet
        // Update local OCR result with edited values and mark verified
        if (ocrResult) {
          finalOcr = {
            ...ocrResult,
            vendorName: editVendorName || ocrResult.vendorName,
            vendorVatNumber: editVatNumber || ocrResult.vendorVatNumber,
            invoiceNumber: editInvoiceNumber || ocrResult.invoiceNumber,
            invoiceDate: editInvoiceDate || ocrResult.invoiceDate,
            totalAmount: editTotalAmount ? parseFloat(editTotalAmount) : ocrResult.totalAmount,
            overallConfidence: 1.0,
          };
          setOcrResult(finalOcr);
        }
        setVerified(true);
        setEditMode(false);
      }

      // Add to scanned invoices list
      if (finalOcr) {
        setScannedInvoices(prev => [...prev, {
          vendorName: editVendorName || finalOcr!.vendorName,
          totalAmount: editTotalAmount ? parseFloat(editTotalAmount) : finalOcr!.totalAmount,
          invoiceNumber: editInvoiceNumber || finalOcr!.invoiceNumber,
          invoiceDate: editInvoiceDate || finalOcr!.invoiceDate,
          blobUrl: blobUrl || undefined,
          ocrResult: finalOcr!,
        }]);
      }
    } catch (err: any) {
      setOcrError(err?.message || 'שגיאה באימות חשבונית');
    } finally {
      setVerifying(false);
    }
  };

  const handleReset = () => {
    setPreview(null);
    setSelectedFile(null);
    setOcrResult(null);
    setOcrError(null);
    setExpensePublicId(null);
    setVerified(false);
    setEditMode(false);
    setUploadProgress(0);
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return { bg: colors.successLight, text: colors.success, label: 'גבוהה' };
    if (confidence >= 0.5) return { bg: colors.warningLight, text: colors.warning, label: 'בינונית' };
    return { bg: colors.errorLight, text: colors.error, label: 'נמוכה' };
  };

  const getConfidenceIcon = (confidence: number) => {
    if (confidence >= 0.8) return <CheckCircle size={16} color={colors.success} />;
    if (confidence >= 0.5) return <AlertTriangle size={16} color={colors.warning} />;
    return <XCircle size={16} color={colors.error} />;
  };

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: colors.text, marginBottom: '20px' }}>
        סריקת חשבונית
      </h1>

      {/* Camera input */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* File input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* ======== STEP 1: Choose file ======== */}
      {!preview && (
        <div style={{
          backgroundColor: colors.white,
          borderRadius: '14px',
          padding: '48px 24px',
          border: `2px dashed ${colors.border}`,
          textAlign: 'center',
        }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '16px',
            backgroundColor: colors.primaryLight,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Camera size={32} color={colors.primary} />
          </div>
          <FormLabel text="סריקת חשבונית" helpText={teacherInvoiceHelpTexts.upload.tooltip} style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '8px', justifyContent: 'center' }} />
          <p style={{ fontSize: '14px', color: colors.textLight, marginBottom: '4px' }}>
            צילום או בחירת קובץ של חשבונית לסריקה אוטומטית
          </p>
          <HelperText text={teacherInvoiceHelpTexts.upload.helper!} />
          <div style={{ marginBottom: '12px' }} />
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button onClick={handleCameraCapture} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '12px 20px', backgroundColor: colors.primary,
              color: 'white', border: 'none', borderRadius: '10px',
              fontSize: '14px', fontWeight: '500', cursor: 'pointer',
            }}>
              <Camera size={18} /> מצלמה
            </button>
            <button onClick={handleFilePick} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '12px 20px', backgroundColor: colors.primaryLight,
              color: colors.primary, border: `1px solid ${colors.primary}40`,
              borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer',
            }}>
              <FolderOpen size={18} /> קובץ
            </button>
          </div>
        </div>
      )}

      {/* ======== STEP 2: Preview + Upload ======== */}
      {preview && !ocrResult && !uploading && !ocrError && (
        <div>
          <div style={{
            backgroundColor: colors.white, borderRadius: '14px',
            padding: '16px', border: `1px solid ${colors.border}`, marginBottom: '16px',
          }}>
            <img src={preview} alt="חשבונית" style={{
              width: '100%', borderRadius: '8px', maxHeight: '300px', objectFit: 'contain',
            }} />
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleReset} style={{
              flex: 1, padding: '12px', backgroundColor: colors.white,
              color: colors.textLight, border: `1px solid ${colors.border}`,
              borderRadius: '10px', fontSize: '14px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            }}>
              <RotateCcw size={16} /> צילום חדש
            </button>
            <button onClick={handleUploadAndScan} style={{
              flex: 2, padding: '12px',
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
              color: 'white', border: 'none', borderRadius: '10px',
              fontSize: '14px', fontWeight: '600', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            }}>
              <Sparkles size={18} /> סריקה אוטומטית
            </button>
          </div>
        </div>
      )}

      {/* ======== UPLOADING / SCANNING ======== */}
      {uploading && (
        <div>
          {preview && (
            <div style={{
              backgroundColor: colors.white, borderRadius: '14px',
              padding: '16px', border: `1px solid ${colors.border}`, marginBottom: '16px',
              opacity: 0.6,
            }}>
              <img src={preview} alt="חשבונית" style={{
                width: '100%', borderRadius: '8px', maxHeight: '200px', objectFit: 'contain',
              }} />
            </div>
          )}
          <div style={{
            backgroundColor: colors.white, borderRadius: '14px',
            padding: '32px 24px', border: `1px solid ${colors.border}`, textAlign: 'center',
          }}>
            <Loader2 size={36} color={colors.primary} style={{ animation: 'spin 1s linear infinite', marginBottom: '16px' }} />
            <p style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
              {uploadProgress < 50 ? 'מעלה חשבונית...' : 'סורק עם Gemini AI...'}
            </p>
            <p style={{ fontSize: '13px', color: colors.textLight, marginBottom: '16px' }}>
              {uploadProgress < 50 ? 'מעביר את התמונה לשרת' : 'מזהה פרטי חשבונית בעברית'}
            </p>
            {/* Progress bar */}
            <div style={{
              height: '6px', backgroundColor: '#e5e7eb', borderRadius: '3px', overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', width: `${uploadProgress}%`,
                background: `linear-gradient(90deg, ${colors.primary}, ${colors.primaryDark})`,
                borderRadius: '3px', transition: 'width 0.3s ease',
              }} />
            </div>
          </div>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* ======== ERROR ======== */}
      {ocrError && !uploading && (
        <div>
          {preview && (
            <div style={{
              backgroundColor: colors.white, borderRadius: '14px',
              padding: '16px', border: `1px solid ${colors.border}`, marginBottom: '16px',
            }}>
              <img src={preview} alt="חשבונית" style={{
                width: '100%', borderRadius: '8px', maxHeight: '200px', objectFit: 'contain',
              }} />
            </div>
          )}
          <div style={{
            backgroundColor: colors.errorLight, borderRadius: '14px',
            padding: '24px', border: `1px solid ${colors.error}30`,
            textAlign: 'center', marginBottom: '16px',
          }}>
            <XCircle size={32} color={colors.error} style={{ marginBottom: '8px' }} />
            <p style={{ fontSize: '15px', fontWeight: '600', color: colors.error, marginBottom: '6px' }}>
              הסריקה נכשלה
            </p>
            <p style={{ fontSize: '13px', color: colors.error + 'cc' }}>{ocrError}</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleReset} style={{
              flex: 1, padding: '12px', backgroundColor: colors.white,
              color: colors.textLight, border: `1px solid ${colors.border}`,
              borderRadius: '10px', fontSize: '14px', cursor: 'pointer',
            }}>
              צילום חדש
            </button>
            <button onClick={handleUploadAndScan} style={{
              flex: 1, padding: '12px', backgroundColor: colors.primary,
              color: 'white', border: 'none', borderRadius: '10px',
              fontSize: '14px', fontWeight: '500', cursor: 'pointer',
            }}>
              נסה שוב
            </button>
          </div>
        </div>
      )}

      {/* ======== OCR RESULTS ======== */}
      {ocrResult && !uploading && (
        <div>
          {/* Mini preview */}
          {preview && (
            <div style={{
              backgroundColor: colors.white, borderRadius: '14px',
              padding: '12px', border: `1px solid ${colors.border}`, marginBottom: '12px',
            }}>
              <img src={preview} alt="חשבונית" style={{
                width: '100%', borderRadius: '8px', maxHeight: '180px', objectFit: 'contain',
              }} />
            </div>
          )}

          {/* Confidence Banner */}
          {!verified && (
            <div style={{
              backgroundColor: getConfidenceColor(ocrResult.overallConfidence).bg,
              borderRadius: '12px', padding: '12px 16px',
              display: 'flex', alignItems: 'center', gap: '10px',
              marginBottom: '12px',
              border: `1px solid ${getConfidenceColor(ocrResult.overallConfidence).text}30`,
            }}>
              {getConfidenceIcon(ocrResult.overallConfidence)}
              <div style={{ flex: 1 }}>
                <span style={{
                  fontSize: '14px', fontWeight: '600',
                  color: getConfidenceColor(ocrResult.overallConfidence).text,
                }}>
                  רמת ודאות: {getConfidenceColor(ocrResult.overallConfidence).label}
                  {' '}({Math.round(ocrResult.overallConfidence * 100)}%)
                </span>
                <p style={{ fontSize: '12px', color: colors.textLight, margin: '2px 0 0' }}>
                  {ocrResult.overallConfidence >= 0.8
                    ? 'הנתונים נראים מדויקים — אשרו או תקנו'
                    : 'מומלץ לבדוק ולתקן את הנתונים'
                  }
                </p>
              </div>
              {!editMode && !verified && (
                <button onClick={() => setEditMode(true)} style={{
                  padding: '6px 10px', backgroundColor: 'transparent',
                  border: `1px solid ${getConfidenceColor(ocrResult.overallConfidence).text}50`,
                  borderRadius: '8px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '4px',
                  color: getConfidenceColor(ocrResult.overallConfidence).text,
                  fontSize: '12px', fontWeight: '500',
                }}>
                  <Edit3 size={14} /> עריכה
                </button>
              )}
            </div>
          )}

          {/* Verified Banner */}
          {verified && (
            <div style={{
              backgroundColor: colors.successLight, borderRadius: '12px',
              padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '10px',
              marginBottom: '12px', border: `1px solid ${colors.success}30`,
            }}>
              <CheckCircle size={20} color={colors.success} />
              <span style={{ fontSize: '15px', fontWeight: '600', color: colors.success }}>
                החשבונית אומתה בהצלחה! ✓
              </span>
            </div>
          )}

          {/* OCR Fields */}
          <div style={{
            backgroundColor: colors.white, borderRadius: '14px',
            padding: '20px', border: `1px solid ${colors.border}`, marginBottom: '12px',
          }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', color: colors.text, marginBottom: '16px' }}>
              פרטי חשבונית
            </h3>

            {/* Vendor Name */}
            <FieldRow
              label="שם העסק/ספק"
              value={editMode ? editVendorName : (ocrResult.vendorName || '—')}
              editable={editMode}
              onChange={setEditVendorName}
              helpText={teacherInvoiceHelpTexts.vendorName.tooltip}
            />

            {/* VAT Number */}
            <FieldRow
              label="ח.פ./עוסק מורשה"
              value={editMode ? editVatNumber : (ocrResult.vendorVatNumber || '—')}
              editable={editMode}
              onChange={setEditVatNumber}
              helpText={teacherInvoiceHelpTexts.vatNumber.tooltip}
              helperText={teacherInvoiceHelpTexts.vatNumber.helper!}
            />

            {/* Invoice Number */}
            <FieldRow
              label="מספר חשבונית"
              value={editMode ? editInvoiceNumber : (ocrResult.invoiceNumber || '—')}
              editable={editMode}
              onChange={setEditInvoiceNumber}
              helpText={teacherInvoiceHelpTexts.invoiceNumber.tooltip}
            />

            {/* Invoice Date */}
            <FieldRow
              label="תאריך"
              value={editMode ? editInvoiceDate : (ocrResult.invoiceDate ? formatDate(ocrResult.invoiceDate) : '—')}
              editable={editMode}
              onChange={setEditInvoiceDate}
              type={editMode ? 'date' : 'text'}
              helpText={teacherInvoiceHelpTexts.invoiceDate.tooltip}
            />

            {/* Divider */}
            <div style={{ borderTop: `1px solid ${colors.border}`, margin: '14px 0' }} />

            {/* Amounts */}
            {ocrResult.totalBeforeVat != null && (
              <FieldRow label="לפני מע״מ" value={`₪${ocrResult.totalBeforeVat.toFixed(2)}`} />
            )}
            {ocrResult.vatAmount != null && (
              <FieldRow label="מע״מ" value={`₪${ocrResult.vatAmount.toFixed(2)}`} />
            )}

            {/* Total — editable */}
            <div style={{
              padding: '10px 0',
              borderTop: ocrResult.totalBeforeVat != null ? `1px solid ${colors.border}` : 'none',
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <FormLabel text="סה״כ" helpText={teacherInvoiceHelpTexts.totalAmount.tooltip} style={{ fontSize: '15px', fontWeight: '700', color: colors.text, marginBottom: 0 }} />
                {editMode ? (
                  <input
                    type="number"
                    step="0.01"
                    value={editTotalAmount}
                    onChange={e => setEditTotalAmount(e.target.value)}
                    style={{
                      width: '140px', padding: '8px 12px', textAlign: 'left',
                      border: `2px solid ${colors.primary}`, borderRadius: '8px',
                      fontSize: '16px', fontWeight: '700', color: colors.primary,
                      outline: 'none', direction: 'ltr',
                    }}
                  />
                ) : (
                  <span style={{ fontSize: '18px', fontWeight: '700', color: colors.primary }}>
                    {ocrResult.totalAmount != null ? `₪${ocrResult.totalAmount.toFixed(2)}` : '—'}
                  </span>
                )}
              </div>
              <HelperText text={teacherInvoiceHelpTexts.totalAmount.helper!} />
            </div>
          </div>

          {/* Items Table */}
          {ocrResult.items && ocrResult.items.length > 0 && (
            <div style={{
              backgroundColor: colors.white, borderRadius: '14px',
              padding: '16px', border: `1px solid ${colors.border}`, marginBottom: '12px',
            }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '12px' }}>
                פריטים ({ocrResult.items.length})
              </h4>
              {/* Table header */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '6px 0', borderBottom: `1px solid ${colors.border}`,
                marginBottom: '4px',
              }}>
                <span style={{ fontSize: '11px', color: colors.textLight, fontWeight: '600', flex: 1 }}>תיאור</span>
                <span style={{ fontSize: '11px', color: colors.textLight, fontWeight: '600', width: '40px', textAlign: 'center' }}>כמות</span>
                <span style={{ fontSize: '11px', color: colors.textLight, fontWeight: '600', width: '65px', textAlign: 'left' }}>מחיר</span>
                <span style={{ fontSize: '11px', color: colors.textLight, fontWeight: '600', width: '65px', textAlign: 'left' }}>סה״כ</span>
              </div>
              {ocrResult.items.map((item, idx) => (
                <div key={idx} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '7px 0',
                  borderTop: idx > 0 ? `1px solid ${colors.border}30` : 'none',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: '13px', color: colors.text, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.description || `פריט ${idx + 1}`}
                    </span>
                  </div>
                  <span style={{ fontSize: '12px', color: colors.textLight, width: '40px', textAlign: 'center' }}>
                    {item.quantity ?? 1}
                  </span>
                  <span style={{ fontSize: '12px', color: colors.textLight, width: '65px', textAlign: 'left', direction: 'ltr' as const }}>
                    {item.unitPrice != null ? `₪${item.unitPrice.toFixed(2)}` : '—'}
                  </span>
                  <span style={{ fontSize: '13px', fontWeight: '500', color: colors.text, width: '65px', textAlign: 'left', direction: 'ltr' as const }}>
                    {item.totalPrice != null ? `₪${item.totalPrice.toFixed(2)}` : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Processed By */}
          <div style={{
            textAlign: 'center', padding: '6px 0', marginBottom: '12px',
          }}>
            <span style={{ fontSize: '11px', color: colors.textLight }}>
              עובד ע״י {ocrResult.processedBy || 'Gemini Flash'}
            </span>
          </div>

          {/* Action Buttons */}
          {!verified && (
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={handleReset} style={{
                flex: 1, padding: '12px', backgroundColor: colors.white,
                color: colors.textLight, border: `1px solid ${colors.border}`,
                borderRadius: '10px', fontSize: '14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              }}>
                <RotateCcw size={16} /> סריקה חדשה
              </button>
              <button
                onClick={handleVerify}
                disabled={verifying}
                style={{
                  flex: 2, padding: '12px',
                  background: `linear-gradient(135deg, ${colors.success}, #16a34a)`,
                  color: 'white', border: 'none', borderRadius: '10px',
                  fontSize: '14px', fontWeight: '600', cursor: verifying ? 'not-allowed' : 'pointer',
                  opacity: verifying ? 0.7 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                }}
              >
                {verifying ? (
                  <>
                    <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    מאמת...
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    {editMode ? 'שמירה ואימות' : 'אישור חשבונית'}
                  </>
                )}
              </button>
            </div>
          )}

          {/* After verification: scan another + go back */}
          {verified && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button onClick={handleReset} style={{
                width: '100%', padding: '14px',
                background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                color: 'white', border: 'none', borderRadius: '10px',
                fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}>
                <Camera size={18} /> סריקת חשבונית נוספת
              </button>
              <button onClick={() => navigate('/teacher/dashboard')} style={{
                width: '100%', padding: '12px',
                backgroundColor: colors.white,
                color: colors.textLight, border: `1px solid ${colors.border}`,
                borderRadius: '10px', fontSize: '14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}>
                <ArrowRight size={16} /> חזרה לבית
              </button>
            </div>
          )}
        </div>
      )}

      {/* ======== Session Summary — list of scanned invoices ======== */}
      {scannedInvoices.length > 0 && !uploading && (
        <div style={{
          backgroundColor: colors.white, borderRadius: '14px',
          padding: '16px', border: `1px solid ${colors.border}`,
          marginTop: '20px',
        }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FileCheck size={16} color={colors.success} />
            חשבוניות שנסרקו ({scannedInvoices.length})
          </h4>
          {scannedInvoices.map((inv, idx) => (
            <div key={idx} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 0',
              borderTop: idx > 0 ? `1px solid ${colors.border}` : 'none',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: '13px', fontWeight: '500', color: colors.text, display: 'block' }}>
                  {inv.vendorName || 'ספק לא ידוע'}
                </span>
                <span style={{ fontSize: '11px', color: colors.textLight }}>
                  {inv.invoiceNumber ? `#${inv.invoiceNumber}` : ''}
                  {inv.invoiceDate ? ` • ${formatDate(inv.invoiceDate)}` : ''}
                </span>
              </div>
              <span style={{ fontSize: '14px', fontWeight: '600', color: colors.primary, direction: 'ltr' as const }}>
                {inv.totalAmount != null ? `₪${inv.totalAmount.toFixed(2)}` : '—'}
              </span>
            </div>
          ))}
          {/* Total */}
          <div style={{
            borderTop: `2px solid ${colors.primary}30`, paddingTop: '10px', marginTop: '4px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>
              סה״כ
            </span>
            <span style={{ fontSize: '16px', fontWeight: '700', color: colors.primary, direction: 'ltr' as const }}>
              ₪{scannedInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0).toFixed(2)}
            </span>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ============ Sub-components ============

function FieldRow({ label, value, editable, onChange, type, helpText, helperText }: {
  label: string;
  value: string;
  editable?: boolean;
  onChange?: (v: string) => void;
  type?: string;
  helpText?: string;
  helperText?: string;
}) {
  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        {helpText ? (
          <FormLabel text={label} helpText={helpText} style={{ color: colors.textLight, minWidth: '100px', marginBottom: 0 }} />
        ) : (
          <span style={{ fontSize: '13px', color: colors.textLight, minWidth: '100px' }}>
            {label}
          </span>
        )}
        {editable && onChange ? (
          <input
            type={type || 'text'}
            value={value}
            onChange={e => onChange(e.target.value)}
            style={{
              flex: 1, maxWidth: '200px', padding: '6px 10px',
              border: `1.5px solid ${colors.primary}60`, borderRadius: '8px',
              fontSize: '14px', color: colors.text, outline: 'none',
              textAlign: 'left', direction: 'ltr' as const,
            }}
            onFocus={e => e.target.style.borderColor = colors.primary}
            onBlur={e => e.target.style.borderColor = `${colors.primary}60`}
          />
        ) : (
          <span style={{ fontSize: '14px', fontWeight: '500', color: colors.text, direction: 'ltr' as const }}>
            {value}
          </span>
        )}
      </div>
      {helperText && <HelperText text={helperText} />}
    </div>
  );
}

function formatDate(dateStr: string): string {
  try {
    const d = dateStr.split('T')[0]; // YYYY-MM-DD
    const parts = d.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return d;
  } catch {
    return dateStr;
  }
}
